/**
 * @file 校区相关 API（对齐「思犇优学」`/pc/campus` 文档）
 * @description 校区列表 / 详情 / 增删改 / 启禁 / 重置密码 / 变更点数 / 充值记录。
 *
 * @whenToUse 校区管理页及校区选择器数据源。
 * @whenNotToUse 教师 / 学员接口（各自独立 api 文件）。
 */

import { del, get, getPage, post, put } from '@/lib/client.ts'

/** 校区列表行（分页） */
export type CampusItem = {
  /** 校区 ID（后端 int64 雪花 id，JSON 序列化为字符串） */
  id: string
  /** 校区名称 */
  campusName: string
  /** 主账号 */
  userName: string
  /** 主账号用户 id */
  userId: string
  /** 点数 */
  points: number
  /** 状态（0-禁用 1-正常） */
  status: string
  /** 教师人数 */
  teacherCount: number
  /** 学员人数 */
  studentCount: number
  /** 创建时间 */
  createTime: string
}

/** 校区列表查询参数 */
export type CampusListParams = {
  /** 页码，从 1 开始 */
  pageNum?: number
  /** 每页条数 */
  pageSize?: number
  /** 校区名称关键字 */
  campusName?: string
  /** 主账号关键字 */
  userName?: string
}

/** 创建校区请求体 */
export type CampusCreatePayload = {
  /** 校区名称 */
  campusName: string
  /** 主账号 */
  userName: string
  /** 初始点数 */
  points: number
  /** 登录密码；为空则后端默认 123456 */
  password?: string
}

/** 编辑校区请求体 */
export type CampusEditPayload = CampusCreatePayload & {
  /** 校区 ID */
  id: string
}

/** 充值记录行（分页） */
export type CampusRechargeRecord = {
  /** 记录 ID */
  id: string
  /** 校区 id */
  campusId: string
  /** 校区名称 */
  campusName: string
  /** 记录类型（recharge-充值 edit-编辑） */
  recordType: string
  /** 变更点数；正数为充值，负数为扣除 */
  points: number
  /** 变更前点数 */
  beforePoints: number
  /** 变更后点数 */
  afterPoints: number
  /** 备注 */
  remark: string
  /** 创建时间 */
  createTime: string
}

/**
 * 分页查询校区列表。
 * @param params 分页与筛选（campusName / userName）
 */
export function fetchCampusList(params?: CampusListParams) {
  return getPage<CampusItem>('/pc/campus/list', { params })
}

/**
 * 校区详情。
 * @param id 校区 ID
 */
export function getCampus(id: string) {
  return get<CampusItem>(`/pc/campus/${id}`)
}

/**
 * 创建校区（同时创建校区主账号）。
 * @param payload 校区字段
 */
export function createCampus(payload: CampusCreatePayload) {
  return post<null>('/pc/campus', payload)
}

/**
 * 编辑校区。
 * @param payload 校区字段（含 id）
 */
export function updateCampus(payload: CampusEditPayload) {
  return put<null>('/pc/campus', payload)
}

/**
 * 删除校区（支持批量，逗号分隔）。
 * @param ids 校区 ID 串
 */
export function deleteCampus(ids: string) {
  return del<null>(`/pc/campus/${ids}`)
}

/**
 * 启用 / 禁用校区。
 * @param id 校区 ID
 * @param status 目标状态（0-禁用 1-正常）
 */
export function changeCampusStatus(id: string, status: '0' | '1') {
  return put<null>('/pc/campus/changeStatus', { id, status })
}

/**
 * 重置校区主账号密码（重置为 123456）。
 * @param id 校区 ID
 */
export function resetCampusPwd(id: string) {
  return post<null>('/pc/campus/resetPwd', { id })
}

/**
 * 变更校区点数。
 * @param id 校区 ID
 * @param points 变更点数（正数充值，负数扣减，不能为 0）
 * @param remark 备注
 */
export function changeCampusPoints(id: string, points: number, remark?: string) {
  return post<null>('/pc/campus/changePoints', { id, points, remark })
}

/**
 * 分页查询校区充值记录。
 * @param params 分页与校区筛选
 */
export function fetchCampusRechargeRecords(params: {
  campusId?: string
  pageNum?: number
  pageSize?: number
}) {
  return getPage<CampusRechargeRecord>('/pc/campus/rechargeRecord/list', {
    params,
  })
}
